import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Directive, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ViewModelProperty } from '../models';
import { startWith } from 'rxjs/operators';

@Directive()
export abstract class BaseEdit implements OnDestroy, OnInit {
  public internalControl: AbstractControl | undefined = undefined;
  @Input() controlName = '';
  @Input() groupName = '';
  @Input() iconName = '';
  @Input() toolTipText = '';
  @Input() enableToolTip = true;
  @Input() autofocus = false;
  @Input() width = 400;
  @Input() public form: UntypedFormGroup = new UntypedFormGroup({});

  subscription: Subscription = new Subscription();
  disabled = false;

  /* support for VMP binding */
  private _vmp: ViewModelProperty | undefined = undefined;

  @Input()
  get vmp(): ViewModelProperty | undefined {
    return this._vmp;
  }

  set vmp(vmpValue: ViewModelProperty | undefined) {
    if (this._vmp == vmpValue) {
      return;
    }
    this._vmp = vmpValue;
    this.subscription.add(
      this.vmp?.valueChange
        .pipe(startWith((vmpValue?.value || vmpValue?.initialValue) as unknown))
        .subscribe((next) => this.updateControlValue(next))
    );
  }

  /* unbound operation */

  @Input() placeHolder = '';
  @Input() emptyDisplayValue = '';
  private _value: any = undefined;
  private _initialValue: any = undefined;

  /* Initial Value */
  @Input()
  get initialValue(): any {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp?.initialValue;
    }
    return this._initialValue;
  }

  set initialValue(value: any) {
    if (this.isBound) {
      console.log('initial value not supported in bound mode');
      return;
    }

    if (this._initialValue == value) {
      return;
    }
    this._initialValue = value;
    if (this._value == undefined && value != undefined) {
      // initialize value vith initial value
      this._value = value;
      this.updateControlValue(value);
    }
  }

  /* Value */

  @Input()
  public get value(): any {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp.value;
    }
    return this._value;
  }

  public set value(value: any) {
    this.internalSetValue(value);
    this.updateControlValue(value);
  }

  ngOnInit(): void {
    if (this.form != undefined) {
      this.internalControl = new UntypedFormControl(this.initialValue);
      this.form.addControl(this.controlName, this.internalControl);

      this.subscription.add(
        this.internalControl.valueChanges.subscribe((value) => {
          this.internalSetValue(value);
        })
      );
      this.subscription.add(
        this.internalControl.statusChanges.subscribe((status) => {
          this.disabled = status === 'DISABLED' ? true : false;
        })
      );
    }
    this.getValuesFromVMP();
  }

  /**
   * sets the value of the vmp or the internal value field
   * @param value
   */
  internalSetValue(value: any) {
    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      this._vmp.value = value;
    } else {
      this._value = value;
    }
  }

  private updateControlValue(value: any): void {
    if (this.internalControl == null) {
      return;
    }
    this.internalControl.patchValue(value);
  }

  private getValuesFromVMP(): void {
    if (!this.isBound) {
      return;
    }
    // @ts-ignore vmp can not be undefined, as we check it with isBound
    if (this._vmp.value != undefined) {
      // @ts-ignore value can not be undefined, as we check it with isBound
      this.updateControlValue(this._vmp.value);
      return;
    }
    // @ts-ignore value can not be undefined, as we check it with isBound
    if (this._vmp.initialValue != undefined) {
      // @ts-ignore value can not be undefined, as we check it with isBound
      this.updateControlValue(this._vmp.initialValue);
      return;
    }
  }

  /* methods */

  public get isBound(): boolean {
    return this._vmp != undefined;
  }

  public get isReadOnly(): boolean {
    return this._vmp === undefined || this._vmp.readOnly;
  }

  public get style(): string {
    return this._vmp?.style ?? '';
  }

  public get hasInitialValue(): boolean {
    if (this.isBound) {
      return this._vmp?.initialValue != undefined;
    }
    return (this.initialValue ?? '') != '';
  }

  public hasIcon() {
    return this.iconName != undefined && this.iconName != '';
  }

  getToolTipText(): string {
    if (!this.enableToolTip) {
      return '';
    }
    if (this.toolTipText != undefined && this.toolTipText != '') {
      // allow manual text override vmp
      return this.toolTipText;
    }

    if (this.isBound) {
      // @ts-ignore vmp can not be undefined, as we check it with isBound
      return this._vmp.infoText;
    }
    return '';
  }

  hasValue(): boolean {
    return this.internalControl != undefined && this.internalControl.value != null && this.internalControl.value != '';
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
